/**
 * The yard's ambient motion, which Settings can turn off: the water washing
 * up the shore (ANIMATED_WATER in ui/nest.ts) and the background scrolling
 * slowly past. Each is a choice kept in the browser, like the item view
 * (ui/itemView.ts). Both start on, unless the device asks for reduced motion.
 */

type Motion = 'water' | 'scroll';

const STORAGE_KEYS: Record<Motion, string> = {
  water: 'dinoclicker.waterMotion',
  scroll: 'dinoclicker.backgroundScroll',
};

function stored(which: Motion): boolean {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEYS[which]);
    if (saved === 'on') return true;
    if (saved === 'off') return false;
  } catch {
    // No storage: fall through to the device's default.
  }
  return !(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
}

const on: Record<Motion, boolean> = { water: stored('water'), scroll: stored('scroll') };

/** Whether the water animates; off, it holds its first frame. */
export function waterMoves(): boolean {
  return on.water;
}

/** Whether the background scrolls; off, it holds where it is. */
export function backgroundScrolls(): boolean {
  return on.scroll;
}

/** Sets a preference (from Settings) and remembers it. */
export function setMotion(which: Motion, next: boolean): void {
  on[which] = next;
  try {
    window.localStorage.setItem(STORAGE_KEYS[which], next ? 'on' : 'off');
  } catch {
    // The choice holds for this visit.
  }
}
